import { toast as sonnerToast } from 'sonner';
import type { ExternalToast } from 'sonner';

/**
 * Custom useToast hook using Sonner
 * Drop-in replacement for shadcn's useToast.
 *
 *   const { toast } = useToast();
 *   toast.success('Saved');
 *   toast({ title: 'Success', description: 'Saved' });
 *
 * For template methods (`successTemplates.updated(...)` etc.) use the
 * standalone export instead — those live on the standalone `toast`, not
 * on the hook's return shape:
 *
 *   import { toast } from '@/hooks/useToast';
 *   toast.successTemplates.updated('Profile');
 */

interface ToastProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
}

interface ToastFunction {
  (props: ToastProps): void;
  success: (message: string, data?: ExternalToast) => void;
  error: (message: string, data?: ExternalToast) => void;
  info: (message: string, data?: ExternalToast) => void;
  warning: (message: string, data?: ExternalToast) => void;
}

interface UseToastReturn {
  toast: ToastFunction;
  toasts: never[]; // Empty array for compatibility
  dismiss: () => void;
}

export function useToast(): UseToastReturn {
  // Main toast function - shadcn API compatible
  const toast = ((props: ToastProps) => {
    if (props.variant === 'destructive') {
      if (props.title && props.description) {
        sonnerToast.error(props.description);
      } else {
        sonnerToast.error(props.title || props.description || 'Error');
      }
    } else {
      if (props.title && props.description) {
        sonnerToast.success(props.description);
      } else {
        sonnerToast(props.title || props.description || '');
      }
    }
  }) as ToastFunction;

  // Direct methods for simple messages. `data` forwards Sonner options
  // (description, action, duration, etc.) so callers can do
  // `toast.error('Failed', { description: 'try again' })`.
  toast.success = (message: string, data?: ExternalToast) => sonnerToast.success(message, data);
  toast.error = (message: string, data?: ExternalToast) => sonnerToast.error(message, data);
  toast.info = (message: string, data?: ExternalToast) => sonnerToast.info(message, data);
  toast.warning = (message: string, data?: ExternalToast) => sonnerToast.warning(message, data);

  return {
    toast,
    toasts: [], // For compatibility with old API
    dismiss: () => sonnerToast.dismiss(), // Dismiss all toasts
  };
}

/**
 * Standalone toast function with template methods
 * For use without the hook
 */
export const toast = Object.assign(
  (message: string, data?: ExternalToast) => sonnerToast(message, data),
  {
    success: (message: string, data?: ExternalToast) => sonnerToast.success(message, data),
    error: (message: string, data?: ExternalToast) => sonnerToast.error(message, data),
    info: (message: string, data?: ExternalToast) => sonnerToast.info(message, data),
    warning: (message: string, data?: ExternalToast) => sonnerToast.warning(message, data),

    // Standardized success templates
    successTemplates: {
      saved: () => sonnerToast.success('Changes saved successfully'),
      created: (item: string) => sonnerToast.success(`${item} created successfully`),
      updated: (item: string) => sonnerToast.success(`${item} updated successfully`),
      deleted: (item: string) => sonnerToast.success(`${item} deleted successfully`),
      completed: (action: string) => sonnerToast.success(`${action} completed successfully`),
    },

    // Standardized error templates
    errorTemplates: {
      generic: () => sonnerToast.error('Something went wrong. Please try again.'),
      load: (item: string) => sonnerToast.error(`Failed to load ${item}`),
      save: () => sonnerToast.error('Failed to save changes'),
      create: (item: string) => sonnerToast.error(`Failed to create ${item}`),
      update: (item: string) => sonnerToast.error(`Failed to update ${item}`),
      delete: (item: string) => sonnerToast.error(`Failed to delete ${item}`),
      required: (field: string) => sonnerToast.error(`${field} is required`),
      invalid: (field: string) => sonnerToast.error(`Invalid ${field}`),
    },

    // Standardized info templates
    infoTemplates: {
      pending: (action: string) => sonnerToast.info(`${action} pending`),
      processing: (action: string) => sonnerToast.info(`${action}...`),
    },

    // Standardized warning templates
    warningTemplates: {
      unsavedChanges: () => sonnerToast.warning('You have unsaved changes'),
    },
  }
);
